import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { MatCalendarCellClassFunction } from "@angular/material/datepicker";
import { Router } from "@angular/router";
import * as Highcharts from "highcharts";
import * as moment from "moment";
import { BatteryType, EventService } from "src/app/services/event.service";

interface SignalData {
  signal: string;
  selected: boolean;
  axis: "Primary" | "Secondary" | "Tertiary";
}

interface ChartsConfig {
  graphId: string;
  axisCount: number;
  graphName: string;
  configs: SignalData[];
}
interface GraphTimeStamps {
  startTime: number;
  endTime: number;
}

interface RawDataBySignalPayload {
  test_id: string;
  signals: string[];
  month: string;
  year: string;
  startTime: number;
  endTime: number;
}

@Component({
  selector: "app-graph",
  templateUrl: "./graph.component.html",
  styleUrls: ["./graph.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class GraphComponent implements OnInit {
  constructor(private eventService: EventService, private router: Router) {}

  axisIndex = {
    Primary: 0,
    Secondary: 1,
    Tertiary: 2,
  };

  defaultYAxisConfig: Highcharts.YAxisOptions = {
    title: {
      text: "",
    },
  };

  primaryYAxisConfig: Highcharts.YAxisOptions = {
    title: {
      text: "",
      style: {
        color: "#eb262d",
        fontWeight: "bold",
      },
    },
    labels: {
      style: {
        color: "#eb262d",
        fontWeight: "bold",
      },
    },
    opposite: false,
  };

  secondaryYAxisConfig: Highcharts.YAxisOptions = {
    title: {
      text: "",
      style: {
        color: "#34495e",
        fontWeight: "bold",
      },
    },
    labels: {
      style: {
        color: "#34495e",
        fontWeight: "bold",
      },
    },
    opposite: true,
  };

  tertiaryYAxisConfig: Highcharts.YAxisOptions = {
    title: {
      text: "",
      style: {
        color: "#16a085",
        fontWeight: "bold",
      },
    },
    labels: {
      style: {
        color: "#16a085",
        fontWeight: "bold",
      },
    },
    opposite: true,
  };

  yAxisConfig = [
    this.primaryYAxisConfig,
    this.secondaryYAxisConfig,
    this.tertiaryYAxisConfig,
  ];

  chartsConfig: ChartsConfig[] = [];
  signalsByGraphId: {
    [key: string]: string[];
  };
  testslist: string[] = [];
  isTestDatesReceived: boolean = false;
  datesToHighlight: any[] = [];
  selectedDate = new Date();
  selectedTest: string = null;
  isTestSelected: boolean = false;
  totalGraphsCount: number = 0;
  numOfPlottedGraph: number = 0;
  isAllgraphsRendered: boolean = false;
  isGraphZoomed: boolean = false;
  chrgIvtVsVoltageGraphId = "G-08";
  chrgIvtVsVoltGraphParentKey = "ChargeIVTAhPass";

  analyticsRawDataInfo: {
    testId: string;
    year: string;
    month: string;
    date: string;
    fullDate: Date;
  } = JSON.parse(localStorage.getItem("analyticsRawDataInfo"));

  ngOnInit(): void {
    if (this.analyticsRawDataInfo) {
      this.selectedDate = this.analyticsRawDataInfo.fullDate;
      this.selectedTest = this.analyticsRawDataInfo.testId;
      this.isTestSelected = true;
      localStorage.removeItem("analyticsRawDataInfo");
    }

    this.getEpackEolTestDates();
    this.getEpackEolTestsByDate(this.selectedDate);
    this.getChartsConfig();
  }

  getPayloadMonthAndYear(date: Date) {
    const selectedDate = moment(date).format("YYYY-MM");

    return {
      month: selectedDate.split("-")[1],
      year: selectedDate.split("-")[0],
    };
  }

  getChartsConfig() {
    this.eventService
      .getChartsConfig()
      .subscribe((res: { success: boolean; data: ChartsConfig[] }) => {
        if (res.success) {
          this.chartsConfig = res.data;
          this.eventService
            .getSelectedSignalsByGraphId()
            .subscribe(
              (res: {
                success: boolean;
                data: { [index: string]: string[] };
              }) => {
                if (res.success) {
                  this.signalsByGraphId = res.data;
                  const graphsList = Object.keys(this.signalsByGraphId);
                  this.totalGraphsCount = graphsList.includes(
                    this.chrgIvtVsVoltageGraphId
                  )
                    ? graphsList.length - 1
                    : graphsList.length;

                  this.triggerGraphGeneration({ testId: this.selectedTest });
                }
              }
            );
        }
      });
  }

  resetLoaders() {
    this.numOfPlottedGraph = 0;
    this.isAllgraphsRendered = false;
    const garphsParent: HTMLElement =
      document.getElementById("graphsContainer");
    garphsParent.style.display = "none";
  }

  manageGraphsVisibility() {
    this.numOfPlottedGraph++;
    if (this.numOfPlottedGraph === this.totalGraphsCount) {
      this.isAllgraphsRendered = true;
      const garphsParent: HTMLElement =
        document.getElementById("graphsContainer");
      garphsParent.style.display = "block";
    }
  }

  triggerGraphGeneration(options: {
    testId: string;
    startTime?: number;
    endTime?: number;
  }) {
    this.analyticsRawDataInfo = JSON.parse(
      localStorage.getItem("analyticsRawDataInfo")
    );
    if (options.testId) {
      const { testId, startTime, endTime } = options;
      this.resetLoaders();
      const { month, year } = this.getPayloadMonthAndYear(this.selectedDate);

      const configureChartData = (
        signalData: ChartsConfig,
        responseData: {
          [key: string]: [number, number][];
        }
      ) => {
        const { graphId, graphName, configs: signalConfigs } = signalData;

        const graphData: Highcharts.SeriesOptionsType[] = [];
        const yAxis: Highcharts.YAxisOptions[] = Array(3).fill(
          this.defaultYAxisConfig
        );
        const includedYAxis: string[] = [];

        signalConfigs.forEach((currentSignalData: SignalData, index) => {
          const { axis, selected, signal } = currentSignalData;

          if (selected) {
            // ensure that only selected axis are added to yAxis
            if (!includedYAxis.includes(axis)) {
              if (axis === "Primary") {
                yAxis[0] = this.primaryYAxisConfig;
              } else if (axis === "Secondary") {
                yAxis[1] = this.secondaryYAxisConfig;
              } else {
                yAxis[2] = this.tertiaryYAxisConfig;
              }

              includedYAxis.push(axis);
            }

            const currentData: Highcharts.SeriesOptionsType = {
              type: "spline",
              name: signal,
              data: responseData[signal],
              yAxis: this.axisIndex[axis],
            };

            graphData.push(currentData);
          }
        });

        this.plotChart({
          graphId,
          graphTitle: graphName,
          signalData: signalConfigs,
          yAxis: yAxis,
          data: graphData,
        });
      };

      const getBatteryType = (): BatteryType => {
        const batteryTypeRegexp: Record<BatteryType, RegExp> = {
          hv: /^HV_EOL/,
          piccolo: /^PL_EOL/,
          tg: /^EOL_/,
        };

        for (const [type, marker] of Object.entries(batteryTypeRegexp)) {
          if (marker.test(testId)) return type as BatteryType;
        }
        return null;
      };

      const getBatteryTypeFilter = (batteryType: BatteryType) => {
        const batteryTypeRegexp: Record<BatteryType, RegExp> = {
          hv: /^HV-G/,
          piccolo: /^PL-G/,
          tg: /^G-/,
        };

        return batteryTypeRegexp[batteryType];
      };

      const batteryType = getBatteryType();
      const batteryTypeFilter = getBatteryTypeFilter(batteryType);
      const validGraphIds = Object.keys(this.signalsByGraphId).filter((_) =>
        batteryTypeFilter.test(_)
      );

      this.totalGraphsCount = validGraphIds.length;

      // chrgIvtVsVoltageGraphId (G-08) is not renderred
      if (batteryType === "tg") {
        this.totalGraphsCount--;
      }

      this.generateGraphDOMbyGraphId(validGraphIds);

      validGraphIds.forEach((currentGraphId) => {
        const payload: RawDataBySignalPayload = this.analyticsRawDataInfo
          ? {
              signals: this.signalsByGraphId[currentGraphId],
              startTime: startTime ? startTime : null,
              endTime: endTime ? endTime : null,
              test_id: this.analyticsRawDataInfo.testId,
              month: this.analyticsRawDataInfo.month,
              year: this.analyticsRawDataInfo.year,
            }
          : {
              signals: this.signalsByGraphId[currentGraphId],
              startTime: startTime ? startTime : null,
              endTime: endTime ? endTime : null,
              test_id: testId,
              month,
              year,
            };

        if (currentGraphId !== this.chrgIvtVsVoltageGraphId) {
          const currentChartConfig = this.chartsConfig.find(
            (config) => config.graphId === currentGraphId
          );

          this.eventService.getRawDataBySignals(payload).subscribe(
            (res: {
              success: boolean;
              data: {
                [key: string]: [number, number][];
              };
            }) => {
              if (res.success) {
                configureChartData(currentChartConfig, res.data);
                this.manageGraphsVisibility();
              }
            }
          );
        }
      });
    }
  }
  generateGraphDOMbyGraphId(graphIds: string[]) {
    const garphsParent: HTMLElement =
      document.getElementById("graphsContainer");

    // remove existing UI to ensure hydration is implemented based on battery type
    garphsParent.innerHTML = "";

    graphIds.forEach((graphId) => {
      if (
        !document.getElementById(graphId) &&
        graphId !== this.chrgIvtVsVoltageGraphId
      ) {
        const newGraph = document.createElement("div");
        newGraph.id = graphId;
        newGraph.style.marginTop = "1.5rem";

        newGraph.style.border = "2px solid #80808080";
        newGraph.style.borderRadius = "4px";
        newGraph.style.padding = "4px";

        garphsParent.appendChild(newGraph);
      }
    });
    garphsParent.style.display = "none";
  }

  plotChart(options: {
    graphId: string;
    graphTitle: string;
    signalData: SignalData[];
    yAxis: Highcharts.YAxisOptions[];
    data: Highcharts.SeriesOptionsType[];
  }) {
    const { graphId, data, graphTitle, signalData, yAxis } = options;

    const defaultChartOption: Highcharts.Options = {
      chart: {
        type: "spline",
        zoomType: "x",
        resetZoomButton: {
          position: {
            align: "right",
            verticalAlign: "top",
            x: -10,
            y: -50,
          },
        },
      },
      title: {
        text: graphTitle,
        style: {
          top: "-5px",
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      yAxis: yAxis,
      series: data,
    };

    const timeSeriesOpt: Highcharts.Options = {
      ...defaultChartOption,
      xAxis: [
        {
          type: "datetime",
          crosshair: true,
          title: {
            text: "",
            style: {
              color: "#34495e",
              fontWeight: "bold",
            },
          },
          events: {
            setExtremes: (e) => {
              if (graphId !== this.chrgIvtVsVoltageGraphId) {
                e.preventDefault();
                if (e.max && e.min) {
                  this.isGraphZoomed = true;
                  this.renderChartBySelection({
                    startTime: new Date(e.min).getTime(),
                    endTime: new Date(e.max).getTime(),
                  });
                }
              }
            },
          },
        },
      ],
      tooltip: {
        xDateFormat: "%e %b,%Y @ %H:%M:%S",
        shared: true,
      },
      time: {
        timezoneOffset: -330,
      },
    };

    const chartAgainstParentOption: Highcharts.Options = {
      ...defaultChartOption,
    };

    const chartOption =
      graphId !== this.chrgIvtVsVoltageGraphId
        ? timeSeriesOpt
        : chartAgainstParentOption;

    Highcharts.chart(graphId, chartOption);
  }

  renderChartBySelection(timeStamp: GraphTimeStamps) {
    const { endTime, startTime } = timeStamp;

    if (endTime && startTime) {
      this.triggerGraphGeneration({
        testId: this.selectedTest,
        startTime,
        endTime,
      });
    }
  }

  getEpackEolTestDates() {
    this.datesToHighlight = [];
    this.isTestDatesReceived = false;

    this.eventService.getEpackEolTestDates({}).subscribe(
      (res: { success: boolean; result: string[] }) => {
        if (res.success) {
          this.datesToHighlight = res.result;
          this.isTestDatesReceived = true;
        }
      },
      (error) => {
        console.log("error getting epack eol test dates ");
      }
    );
  }

  getEpackEolTestsByDate(selectedDate: Date) {
    const stringDate = moment(selectedDate).format("YYYY-MM-DD");
    this.testslist = [];
    this.eventService.getEpackEolTestsByDate({ date: stringDate }).subscribe(
      (res: { success: boolean; result: { test_id: string }[] }) => {
        if (res.success) {
          if (res.result.length > 0) {
            this.testslist = res.result.map((test) => test.test_id);
          } else {
            // no tests found means no data is present
            // either warning can be shown or chart can be plotted with empty data
          }
        }
      },
      (error) => {
        console.log("error getting epack eol test dates");
      }
    );
  }

  highlightDates(): MatCalendarCellClassFunction<Date> {
    return (cellDate, view) => {
      // Only highligh dates inside the month view.
      if (view === "month") {
        const date = cellDate.getDate();

        const validHighlight = this.datesToHighlight
          .map((stringDate: string) => new Date(stringDate))
          .some(
            (genereatedDate: Date) =>
              genereatedDate.getDate() === date &&
              genereatedDate.getMonth() === cellDate.getMonth() &&
              genereatedDate.getFullYear() === cellDate.getFullYear()
          );

        return validHighlight ? "special-date" : "";
      }

      return "";
    };
  }

  dateChange(selectedDate: Date) {
    this.selectedTest = null;
    this.isTestSelected = false;
    this.getEpackEolTestsByDate(selectedDate);
  }

  resetGraph() {
    this.isGraphZoomed = false;
    this.triggerGraphGeneration({
      testId: this.selectedTest,
    });
  }

  renderRawDataGraph(testId: string, testDate: Date) {
    const payload = { testId, testDate: moment(testDate).format("DD-MM-YYYY") };
    localStorage.setItem("epackRawDataInfo", JSON.stringify(payload));
    this.router.navigate(["eol/raw-data"]);
  }
}
